import { useState, useEffect } from "react";
import { BeatCard } from "@/components/BeatCard";
import { BeatDetailsModal } from "@/components/BeatDetailsModal";
import { ContactSection } from "@/components/ContactSection";
import { AuthModal } from "@/components/AuthModal";
import { UserMenu } from "@/components/UserMenu";
import { PurchaseHistoryModal } from "@/components/PurchaseHistoryModal";
import { AddBeatModal } from "@/components/AddBeatModal";
import { AdminFeaturedBeatsModal } from "@/components/AdminFeaturedBeatsModal";
import { useAudioPlayer } from "@/components/AudioPlayerProvider";
import { Button } from "@/components/ui/button";
import { ArrowRight, Music } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface Beat {
  id: string;
  title: string;
  producer: string;
  genre: string;
  bpm: number;
  image_url: string;
  mp3_url: string;
  wav_url: string;
  flp_url: string;
  mp3_lease_price: number;
  wav_lease_price: number;
  flp_price: number;
  semi_exclusive_price: number;
  full_exclusive_price: number;
  is_featured: boolean;
  is_sold_exclusive?: boolean;
}

const Index = () => {
  const navigate = useNavigate();
  const audioPlayer = useAudioPlayer();
  const { playBeat, setBeats: setAudioPlayerBeats, currentBeat, isPlaying } = audioPlayer || {};
  const [selectedBeat, setSelectedBeat] = useState<Beat | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [beats, setBeats] = useState<Beat[]>([]);
  const [soldExclusives, setSoldExclusives] = useState<Beat[]>([]);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isPurchaseHistoryOpen, setIsPurchaseHistoryOpen] = useState(false);
  const [isAddBeatModalOpen, setIsAddBeatModalOpen] = useState(false);
  const [isFeaturedBeatsModalOpen, setIsFeaturedBeatsModalOpen] = useState(false);

  useEffect(() => {
    fetchBeats();
    fetchSoldExclusives();
  }, []);

  const handlePlayBeat = (beat: any) => {
    // Don't allow playing sold exclusive beats
    if (beat.is_sold_exclusive) {
      return;
    }
    if (playBeat) {
      playBeat(beat);
    }
  };

  const fetchBeats = async () => {
    const { data } = await supabase
      .from("beats")
      .select("*")
      .eq("is_deleted", false)
      .eq("is_hidden", false)
      .or(`release_date.is.null,release_date.lte.${new Date().toISOString()}`)
      .order("created_at", { ascending: false });
    
    if (data) {
      setBeats(data);
      if (setAudioPlayerBeats) {
        setAudioPlayerBeats(data);
      }
    }
  };

  const fetchSoldExclusives = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('get-sold-exclusives');
      
      if (error) {
        if (import.meta.env.DEV) console.error('Error fetching sold exclusives:', error);
        return;
      }

      const soldExclusiveBeatIds = new Set(data.soldExclusiveBeatIds || []);
      const soldExclusiveBeats = (data.soldExclusives || [])
        .map((purchase: any) => ({
          ...purchase.beats,
          is_sold_exclusive: true
        }))
        .filter((beat: any, index: number, self: any[]) => 
          index === self.findIndex(b => b.id === beat.id)
        );

      setSoldExclusives(soldExclusiveBeats);

      // Mark beats as sold exclusive
      setBeats(prevBeats => 
        prevBeats.map(beat => ({
          ...beat,
          is_sold_exclusive: soldExclusiveBeatIds.has(beat.id)
        }))
      );
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching sold exclusives:', error);
    }
  };

  const handleBeatClick = (beat: Beat) => {
    setSelectedBeat(beat);
    setIsModalOpen(true);
  };

  const featuredBeats = beats.filter(beat => beat.is_featured && !beat.is_sold_exclusive).slice(0, 3);

  return (
    <div className="min-h-screen bg-gradient-dark pb-24">
      {/* Header */}
      <header className="border-b border-border/50 backdrop-blur-sm bg-background/80 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/lovable-uploads/f099ba0d-4461-42b2-9dc9-9b0ff4fc7716.png" alt="Skpills Logo" className="h-8 w-8" />
            <h1 className="text-xl font-bold gradient-text drop-shadow-sm">
              Skpills
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <nav className="hidden md:flex items-center space-x-6">
              <a 
                href="#beats" 
                className="text-muted-foreground hover:text-foreground transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('beats')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Beats
              </a>
              <a 
                href="#contact" 
                className="text-muted-foreground hover:text-foreground transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Contact
              </a>
            </nav>
            <UserMenu 
              onAuthClick={() => setIsAuthModalOpen(true)}
              onPurchasesClick={() => setIsPurchaseHistoryOpen(true)}
              onAddBeatClick={() => setIsAddBeatModalOpen(true)}
              onFeaturedBeatsClick={() => setIsFeaturedBeatsModalOpen(true)}
            />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-primary opacity-10" />
        <div className="container mx-auto px-4 py-24 text-center relative">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <Music className="h-16 w-16 mx-auto text-primary mb-4" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 gradient-text drop-shadow-lg leading-tight">
              Skpills
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Discover premium beats from Skpills.
            </p>

            <div className="flex justify-center">
              <Button 
                onClick={() => navigate("/beats")}
                size="lg" 
                className="group shadow-glow hover:shadow-glow transition-smooth"
              >
                <span>Explore All Beats</span>
                <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-smooth" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Beats Section */}
      <section id="beats" className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
            Featured Beats
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Handpicked newest beats
          </p>
        </div>

        {/* Beat Cards Grid - Featured Layout */}
        <div className="max-w-6xl mx-auto">
          {/* Main Featured Beat */}
          {featuredBeats[0] && (
            <div className="mb-8 animate-fade-in">
              <div className="flex justify-center">
                <div className="w-full max-w-2xl">
                  <BeatCard 
                    beat={featuredBeats[0]}
                    onPlay={() => handlePlayBeat(featuredBeats[0])}
                    isPlaying={currentBeat?.id === featuredBeats[0].id && Boolean(isPlaying)}
                    onClick={() => handleBeatClick(featuredBeats[0])}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Secondary Featured Beats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {featuredBeats.slice(1).map((beat, index) => (
              <div
                key={beat.id}
                className="animate-fade-in"
                style={{ animationDelay: `${(index + 1) * 200}ms` }}
              >
                <BeatCard 
                  beat={beat}
                  onPlay={() => handlePlayBeat(beat)}
                  isPlaying={currentBeat?.id === beat.id && Boolean(isPlaying)}
                  onClick={() => handleBeatClick(beat)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Show More Button */}
        <div className="text-center mt-12">
          <Button
            onClick={() => navigate("/beats")}
            size="lg"
            className="group shadow-glow hover:shadow-glow transition-smooth"
          >
            <span>Explore All Beats</span>
            <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-smooth" />
          </Button>
        </div>
      </section>


      {/* Features Section */}
      <section className="container mx-auto px-4 py-16 border-t border-border/20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div className="p-6">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Music className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-foreground">Premium Quality</h3>
            <p className="text-muted-foreground">
              Professional-grade beats mixed and mastered to perfection
            </p>
          </div>
          <div className="p-6">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <ArrowRight className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-foreground">Instant Download</h3>
            <p className="text-muted-foreground">
              Get your beats instantly after purchase, ready to use
            </p>
          </div>
          <div className="p-6">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Music className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-foreground">Exclusive Rights</h3>
            <p className="text-muted-foreground">
              Purchase exclusive rights and make the beat truly yours
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <ContactSection />

      {/* Beat Details Modal */}
      <BeatDetailsModal 
        beat={selectedBeat}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onBeatDeleted={fetchBeats}
      />

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
      />

      <PurchaseHistoryModal
        isOpen={isPurchaseHistoryOpen}
        onClose={() => setIsPurchaseHistoryOpen(false)}
      />

      <AddBeatModal
        isOpen={isAddBeatModalOpen}
        onClose={() => setIsAddBeatModalOpen(false)}
        onBeatAdded={fetchBeats}
      />

      <AdminFeaturedBeatsModal
        isOpen={isFeaturedBeatsModalOpen}
        onClose={() => setIsFeaturedBeatsModalOpen(false)}
        onBeatsUpdated={fetchBeats}
      />
    </div>
  );
};

export default Index;
